///
/// EarthBrowser
///
/// Created by Matt Giger on 9/3/2026
/// Copyright (c) 2026 Geodata Labs
///

import { type VolcanoConeNode } from "@/earthbrowser/dataset-distribution/volcanoes/VolcanoConeNode"
import { DesignColors } from "@/earthbrowser/design-system/DesignColors"
import { EmberDomeNode } from "@/earthbrowser/entities/EmberDome/EmberDomeNode"
import { NodeBase } from "@stovokor/engine/NodeBase"
import { animation, easing } from "@stovokor/math/animation"
// import { trig } from "@stovokor/math/math"
import { matrix } from "@stovokor/math/matrix"
// import { vec3 } from "@stovokor/math/vector"
import { type StovokorContext } from "@stovokor/StovokorContext"

// Fixed screen-space size floor so volcanoes stay visible from orbit.
// Constant for every volcano (unlike quakes, whose floor varies by recency):
// ~1.6px radius on a 1080p screen
const cVolcanoMinAngular = 0.0012

// Face-on solidity of the dome (0 = rim-only fresnel, 1 = solid ball).
// Volcanoes use a solid fill so they read clearly over bright terrain when
// zoomed in, like the old solid-color wireframe markers.
const cVolcanoFill = 0.65

// Opacity of the far-mode dot at global zoom. Kept low so thousands of
// volcanoes read as quiet amber flecks instead of dominating the globe.
const cVolcanoFarFill = 0.35

/**
 * Composite volcano marker:
 * - An ember dome (fresnel rim-glow sphere) sized exactly to the hit test
 *   radius, so the visible glow communicates the clickable volume
 * - A stylized lava-crater cone that fades in when the volcano is focused
 *
 * The parent node's transform must be radially aligned (the standard
 * xRot(lat + pi/2) / yRot(lon + pi) marker rotation), which leaves local
 * -Y pointing away from the earth's center.
 */
export class VolcanoMarkerNode extends NodeBase {
	// Only needed by the disabled cone block in setFocused
	// private readonly markerContext: StovokorContext
	// private readonly radius: number
	private readonly dome: EmberDomeNode
	private cone?: VolcanoConeNode
	private focusAnim?: animation
	private focusBlend = 0
	private focused = false

	constructor(context: StovokorContext, radius: number, _epoch?: string | null) {
		super()
		// this.markerContext = context
		// this.radius = radius

		// Volcanoes are ambient landmarks, not events: design-system orange,
		// no pulse, and a small fixed size floor so they stay visible from
		// orbit at a constant apparent size for every volcano
		this.dome = new EmberDomeNode(context, DesignColors.MARKER_VOLCANO)
		this.dome.pulse = 0
		this.dome.fillAlpha = cVolcanoFill
		this.dome.farFillAlpha = cVolcanoFarFill
		this.dome.minAngularSize = cVolcanoMinAngular
		this.dome.transform = matrix.scale(radius)
		this.addNode(this.dome)
	}

	/** Update marker styling when feature data changes */
	public setEpoch(_epoch: string | null | undefined): void {
		// Epoch no longer affects styling: all volcanoes use the same orange
	}

	/** Cross-fade between the ambient dome and the focused cone */
	public setFocused(focused: boolean): void {
		if(this.focused === focused) {
			return
		}
		this.focused = focused

		// Stylized cone disabled for now: it hides the actual 3d terrain of the
		// volcano when focused. Re-enable by uncommenting this block.
		// if(focused && !this.cone) {
		// 	this.cone = new VolcanoConeNode(this.markerContext)
		// 	// The cone geometry is +Y up with its base at the origin. Local -Y
		// 	// points radially up, so flip it, then sink it so the crater rim
		// 	// sits just above the summit point and the base emerges from terrain.
		// 	this.cone.transform = matrix.scale(this.radius)
		// 		.multiply(matrix.axisRotation(vec3.unitX, trig.pi))
		// 		.multiply(matrix.translate(0, 0.6 * this.radius, 0))
		// 	this.cone.visible = false
		// 	this.addNode(this.cone)
		// }

		this.focusAnim?.stop()
		this.focusAnim = new animation()
			.from(this.focusBlend)
			.to(focused ? 1 : 0)
			.duration(0.5)
			.easing(easing.quadraticInOut)
			.onUpdate(value => this.applyBlend(value))
		this.focusAnim.start()
	}

	private applyBlend(value: number): void {
		this.focusBlend = value
		if(this.cone) {
			this.cone.opacity = value
			this.cone.visible = value > 0.01
		}
		// Keep a faint dome while focused as a hint of the clickable volume
		this.dome.opacity = 1 - 0.75 * value
	}

	override destroy(): void {
		this.focusAnim?.stop()
		super.destroy()
	}
}
